/**
 * @name Page
 * @description The main layout. Includes sidebar, title bar, and router component.
 * @author James Pearce
 */
import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Container from '@material-ui/core/Container';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ExitToApp from '@material-ui/icons/ExitToApp';
import { mainListItems, secondaryListItems } from './listItems';
import Login from './Login'
import Amplify, { Auth } from 'aws-amplify'
import PageRouter from './PageRouter';



const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
    paddingLeft: '10px',
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    height: `calc(100% - 65px)`
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  fixedHeight: {
    height: 240,
  },
}));

export default function Page() {
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
          >
            <MenuIcon />
          </IconButton>
          <svg style={{fill:'#fff'}} height="35" viewBox="0 0 39.252102 42.402702" xmlns="http://www.w3.org/2000/svg"><path d="M6.603873 37.432551c-.55538-1.810146-1.20053-3.615501-1.92741-5.487017-3.97326-10.201579-4.42222-11.72762-4.63719-15.7619-.18791-3.52657.28871-6.0759 1.64133-8.77915 2.54532-5.0869 8.59557-8.14362 14.35002-7.24996 5.67441.88122 9.13937 4.52826 8.29529 8.73121-.32677 1.62713-.81333 2.41664-.83243 1.35074-.0171-.95205-.62962-2.51637-1.44752-3.69661-1.58434-2.2862-5.26849-3.64701-8.9919-3.32133-2.33169.20394-4.28572 1.12519-6.18107 2.91412-4.26652 4.02699-4.45404 10.91144-.40442 14.84722 3.90157 3.79189 11.47172 5.39679 15.5151 3.28927l1.01898-.53113.67983 1.52407c1.69029 3.960466 3.18733 8.051419 4.45541 12.171934-3.29769 6.375656-19.45904 6.870085-21.53402-.0015zm30.66792-11.100107c-.0143-.77715-.97219-1.06469-2.90133-.87095-2.27596.22856-3.724-.15964-5.04407-1.35223-1.376-1.24313-1.94502-2.79204-1.80921-4.92479.13085-2.05511.67246-2.87965 2.47021-3.76072 2.70716-1.32676 4.38649-1.13488 6.41258.73271 2.3954 2.208 3.36115 5.08257 2.59406 7.72125-.4386 1.50874-1.70636 3.31571-1.72224 2.45473zm-23.57662-12.62281c-.52784-.52784-.77341-1.03834-.77341-1.60785 0-1.1772 1.24113-2.38125 2.45457-2.38125 1.34546 0 2.30794.99305 2.30794 2.38125 0 2.21868-2.40779 3.18916-3.9891 1.60785z"/></svg>
          <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}>
            DevOps Dashboard
          </Typography>
          <IconButton color="inherit" onClick={()=>{Auth.signOut()}}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
        }}
        open={open}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>{mainListItems}</List>
        <Divider />
        <List>{secondaryListItems}</List>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container} fixed>
          { /* content here */ }
          <PageRouter/>
        </Container>
      </main>
    </div>
  );
}
